'use client';

// 'use no memo' — React Compiler escape hatch (reactCompiler:true in next.config.mjs).
// This swipe deck is the compiler-fragile case: it relies on (a) a mutable `resolverRef` whose
// cleanup is IDENTITY-GUARDED across AnimatePresence's late exit unmount, and (b) live
// `useMotionValue`/`useTransform` channels read straight into `style` during drag. The React
// Compiler's auto-memoization can hoist/cache the registration callbacks and the motion.div props in
// ways that silently break the identity guard (✕/✓ buttons go dead after the first swipe) or freeze
// the drag transform. Opting THIS file out of the compiler keeps the imperative ref + motion-value
// wiring exactly as authored. Remove only if the drag is re-expressed without the late-cleanup
// identity guard. (Directive must sit at the top of the module, after 'use client'.)
'use no memo';

import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Check, X } from 'lucide-react';
import type { PanInfo } from 'motion/react';
import { AnimatePresence, motion, useMotionValue, useTransform } from 'motion/react';
import { cn } from '@/utils/cn';
import { useLabsStore, usePack } from '../pack-context';
import type { LabsSwipeCard } from '../types';
import { StepNav } from './_components';
import { StepShell } from './step-shell';

/**
 * M1 · Activity — the "A Day in the Life" swipe deck, rendered through the fs-* design system
 * (fs-* tokens + DM Serif Display / Inter).
 *
 * LAYOUT:
 *   - A centered header: eyebrow, serif headline, helper line.
 *   - A centered card STACK (max-w 380, h 420): the front card is fully visible; the two behind
 *     peek at the BOTTOM via translateY + scale-down + reduced opacity (depth-1: +8px/0.96/0.7,
 *     depth-2: +16px/0.92/0.4).
 *   - A card is white, rounded-[20px], with a big emoji icon (~4.5rem), a serif activity line
 *     (~1.9rem), and a muted "Swipe or tap a button" hint at the bottom.
 *   - BELOW the deck: a direction helper ("← no · yes →"), then the controls row (round ✕ button ·
 *     "n / total" mono counter · round ✓ button), then a quiet text "Skip" link.
 *
 * BEHAVIOUR: the front card is draggable (motion); past ±DRAG_THRESHOLD it throws off-screen and
 * records right/left via the store (`setM1Answer`, keyed by CARD ID — the deck's order is content,
 * not identity, so a pack may reorder its cards without invalidating a persisted draft). The ✕/✓
 * buttons trigger the same throw on the front card. Skip advances WITHOUT recording a yes/no. When
 * the deck is exhausted, advance to `m1-results`.
 *
 * M1 is ADVISORY ONLY: the recorded lean drives the m1-results copy + lean bar, but does NOT set the
 * archetype and gates nothing downstream (M2 is the branch point).
 *
 * Everything on this screen except the eyebrow and the helper line is INVARIANT CHROME — the swipe
 * instruction, the YES/PASS stamps, the counter, Skip/Back — because the MECHANIC (a two-way swipe
 * on a deck) is the same in every vertical; only the activities on the cards change.
 *
 * Token mapping (design intent → fs-* token):
 *   - card surface #FFFFFF + #E6E4DD          → `fs-surface` + `fs-border`
 *   - YES overlay green #16A34A / rgba .15    → `fs-success` + `fs-success-subtle`
 *   - PASS overlay red #DC2626 / rgba .12     → `fs-danger` + `fs-danger-subtle`
 *   - "← no" / "yes →" helper #DC2626/#16A34A → `fs-danger` / `fs-success`
 *   - counter / hint mute #6B7280             → `fs-text-muted` / `fs-icon-subtle`
 */

const DRAG_THRESHOLD = 100; // px — the swipe commit distance.

/** Front-card resolver registered up to the parent so the below-deck buttons can throw it. */
type Resolver = (direction: 'left' | 'right') => void;
export function M1Activity(): ReactNode {
  const {
    m1,
    copy
  } = usePack();
  const setM1Answer = useLabsStore(s => s.setM1Answer);
  const m1Answers = useLabsStore(s_0 => s_0.m1Answers);
  const m1Cursor = useLabsStore(s_1 => s_1.m1Cursor);
  const setM1Cursor = useLabsStore(s_2 => s_2.setM1Cursor);
  const next = useLabsStore(s_3 => s_3.next);
  const back = useLabsStore(s_4 => s_4.back);
  const cards = m1.cards;
  const deckSize = cards.length;

  // Index of the current top card. Derived once on mount from any persisted answers so a refresh
  // mid-deck resumes where the user left off (the store is the source of truth for the lean).
  const [cursor, setCursor] = useState(() => {
    let i = 0;
    while (i < deckSize) {
      const card = cards[i];
      if (!card || m1Answers[card.id] === undefined) {
        break;
      }
      i += 1;
    }
    // Resume past SKIPPED cards too: a skip advances the deck WITHOUT writing to `m1Answers`, so the
    // answer-derived index alone replays skipped cards after a refresh. The persisted `m1Cursor` (the
    // furthest resolved position, skips included) carries that forward — take whichever is further.
    return Math.max(i, m1Cursor);
  });

  // True iff the deck was ALREADY fully resolved when this screen mounted. A forward completion
  // advances the STEP to m1-results (that's what persists), so the only way to land on m1-activity with
  // a finished deck is pressing Back from m1-results. There we must NOT auto-advance forward (the old
  // "Back immediately re-forwards" trap) — we render a completed state with StepNav instead.
  const [completeOnMount] = useState(() => cursor >= deckSize);
  const advance = (): void => {
    // Keep the state updater PURE (no side effects — React StrictMode double-invokes updaters in
    // dev, which would fire next() twice). Read the live cursor from the setter's argument only to
    // compute the next value; do the deck-exhausted nav in a separate, idempotent guard below.
    setCursor(c => Math.min(c + 1, deckSize));
  };

  // Persist the resume position (skips included) so a refresh resumes past skipped cards. Kept OUT of
  // the pure `advance` updater (StrictMode double-invokes updaters); this idempotent store write just
  // mirrors `cursor` whenever it changes.
  useEffect(() => {
    setM1Cursor(cursor);
  }, [cursor, setM1Cursor]);

  // When the deck is exhausted (cursor past the last card), advance to m1-results exactly once.
  // The ref guard makes this idempotent under React StrictMode's double-invoked effects in dev.
  const advancedRef = useRef(false);
  // Fires only when the deck boundary is crossed DURING this mount. We skip it entirely when the deck
  // was already complete on mount (a Back-revisit), so Back from m1-results no longer bounces straight
  // back. `next`/`completeOnMount`/`deckSize` are stable; the `advancedRef` guard keeps the body
  // idempotent under StrictMode's double-invoked effects. Listed to satisfy biome's
  // `useExhaustiveDependencies`.
  useEffect(() => {
    if (completeOnMount) {
      return;
    }
    if (cursor >= deckSize && !advancedRef.current) {
      advancedRef.current = true;
      next();
    }
  }, [completeOnMount, cursor, deckSize, next]);
  const recordSwipe = (card_0: LabsSwipeCard, direction: 'left' | 'right'): void => {
    setM1Answer(card_0.id, direction);
    advance();
  };

  // Front-card resolver registration: the active SwipeCard registers its `resolve` here so the
  // below-deck ✕/✓ buttons (and Skip) can throw the SAME card with the SAME eased exit + recording.
  // IMPORTANT: registration is IDENTITY-GUARDED. AnimatePresence keeps a just-swiped card mounted
  // through its ~280ms exit, so that card's effect cleanup fires LATE — after the next front card has
  // already registered. A naive `registerResolver(null)` cleanup would then clobber the new card's
  // registration (the symptom: the ✕/✓ buttons go dead after the first swipe). So the cleanup only
  // clears the ref when it STILL points at this card's own resolver.
  const resolverRef = useRef<Resolver | null>(null);
  const setResolver = useCallback((fn: Resolver) => {
    resolverRef.current = fn;
  }, []);
  const clearResolver = useCallback((fn_0: Resolver) => {
    // Identity guard: only clear if the ref still points at THIS card's resolver.
    if (resolverRef.current === fn_0) {
      resolverRef.current = null;
    }
  }, []);

  // Top-3 visible cards (depth 0/1/2) — the stacked deck.
  const visible = cards.slice(cursor, cursor + 3);
  const remaining = deckSize - cursor;
  // Deck finished AND arrived via Back (completeOnMount): render a completed state with StepNav instead
  // of an empty card stack + dead (disabled) controls.
  const deckComplete = remaining <= 0 && completeOnMount;

  // The swipe cards are this step's discrete TASKS: `cursor` = cards resolved (swipe / button / Skip
  // all count), passed up as the intra-screen task count so the top progress bar advances ONE unit
  // per card across M1's 25% slice, instead of sitting frozen until the deck is exhausted.

  // Marks the next resolve as a Skip so it advances the deck WITHOUT writing a yes/no to the store
  // (the Tinder model). The left-fly exit is reused purely as motion.
  const skipRef = useRef(false);
  const triggerButton = (direction_0: 'left' | 'right'): void => {
    resolverRef.current?.(direction_0);
  };
  const onSkip = (): void => {
    if (remaining <= 0) {
      return;
    }
    skipRef.current = true;
    resolverRef.current?.('left'); // reuse the left throw as motion only; not recorded
  };
  const resolveSwipe = (card_1: LabsSwipeCard, direction_1: 'left' | 'right'): void => {
    if (skipRef.current) {
      skipRef.current = false;
      advance(); // skip: advance the deck without recording the lean
      return;
    }
    recordSwipe(card_1, direction_1);
  };

  // Chrome (the compact progress pill/% and the shared top-left "← Back") is owned by StepShell, so
  // M1 only supplies its body. `tasksCompleted={cursor}` feeds the per-card task count (cards
  // resolved) up to the shared ProgressBar so the bar advances one unit on every resolved card.
  return <StepShell contentClassName="flex flex-col items-center" tasksCompleted={cursor}>
      {/* Header — eyebrow + serif headline + helper, centered. The headline states the MECHANIC, so
          it is the same in every vertical; the eyebrow and helper name the domain. */}
      <header className="mt-1 mb-2 flex w-full flex-col items-center gap-2 text-center">
        <div className="font-medium text-[11px] text-fs-text-muted uppercase tracking-[0.12em]">
          {copy.m1.activity.eyebrow}
        </div>
        <h1 className="font-instrument text-[34px] text-fs-text leading-[1.1] tracking-[-0.5px] sm:text-[40px]">
          Swipe right if this is interesting to you. Left if it&apos;s not.
        </h1>
        <p className="text-fs-body-lg text-fs-text-subtle">{copy.m1.activity.helper}</p>
      </header>

      {/* Card stack — centered, max-w 380, h 420. The front card is fully visible; the two
          behind peek at the BOTTOM (translateY + scale-down). Absolute children keep the
          footprint fixed while the front card drags/throws (so the controls never reflow). */}
      {/* The deck is the tallest thing in the sprint. At 393x852 the desktop card (420px) plus the
          desktop gaps put the yes/pass controls 3px off the viewport floor — and a real iPhone shows
          ~745px once Safari's toolbars are up, so the primary controls would start off-screen. The
          card and the gaps below therefore shrink on mobile and restore at `sm:`.
           🔴 AND `sm:` IS A WIDTH QUERY, WHICH IS WHY A FLAT 420px WAS STILL WRONG. A 1280x800 laptop
          is `sm:` by width and short by height, so it took the full desktop card and rendered the
          screen 929px tall inside an 800px viewport: the yes/pass controls landed 25px BELOW the fold
          on the first screen a guide-page reader sees, with nothing above the fold telling them the
          card can be dragged. `StepShell` sets a MIN height, not a max, so nothing upstream clamps
          this. Measured 2026-08-30 at 1280x800; mobile and 1440x900 were already fine.
           The cap keeps the 420px design wherever it fits and trades card size for reachable controls
          where it does not. 26rem is the measured chrome above and below the deck (header, headline,
          helper, controls, counter, nav) plus a small margin. */}
      <div className="relative mx-auto mt-4 flex h-[320px] w-full max-w-[380px] items-center justify-center sm:mt-6 sm:h-[min(420px,calc(100dvh-26rem))]">
        {deckComplete ? <p className="px-6 text-center font-instrument text-[26px] text-fs-text leading-[1.15] tracking-[-0.5px]">
            You&apos;ve gone through all {deckSize}.
          </p> : <AnimatePresence initial={false}>
            {visible.map((card_2, i_0) => <SwipeCard card={card_2} clearResolver={i_0 === 0 ? clearResolver : undefined} depth={i_0} interactive={i_0 === 0} key={card_2.id} onResolve={dir => resolveSwipe(card_2, dir)} setResolver={i_0 === 0 ? setResolver : undefined} />).reverse() /* render back-to-front so depth-0 (front) paints on top */}
          </AnimatePresence>}
      </div>

      {deckComplete ? <div className="mt-6 w-full max-w-[380px]">
          <StepNav onNext={next} />
        </div> : <>
          {/* Direction helper */}
          <p className="mt-2 text-center text-[11px] text-fs-text-muted uppercase tracking-[0.15em] sm:mt-4">
            <span className="text-fs-danger">← no</span>
            <span className="mx-2 opacity-60">·</span>
            <span className="text-fs-success">yes →</span>
          </p>

          {/* Controls row: round ✕ · "n / total" counter · round ✓ */}
          <div className="mt-4 flex items-center justify-center gap-6 sm:mt-6">
            <ActionButton disabled={remaining <= 0} label="Not for me" onClick={() => triggerButton('left')} tone="no">
              <X className="h-6 w-6" strokeWidth={2} />
            </ActionButton>

            <div className="min-w-[50px] text-center font-mono text-[12px] text-fs-text-muted tabular-nums">
              {cursor} / {deckSize}
            </div>

            <ActionButton disabled={remaining <= 0} label="I'd want this day" onClick={() => triggerButton('right')} tone="yes">
              <Check className="h-6 w-6" strokeWidth={2} />
            </ActionButton>
          </div>

          {/* Bottom row — on THIS screen only, "Back" is a quiet text LINK (not a button) so it matches
              the "Skip" link's aesthetic; there's no "Continue" here (the deck auto-advances once every
              card is resolved). The ✕/✓ controls + counter stay centered above. */}
          <div className="mt-3 flex w-full max-w-[380px] items-center justify-between gap-3">
            <button className="inline-flex min-h-[44px] cursor-pointer items-center gap-1 px-2 py-2 text-fs-body-md text-fs-text-muted underline-offset-4 transition-colors hover:text-fs-text hover:underline" onClick={back} type="button">
              <ArrowLeft className="h-4 w-4" strokeWidth={2} />
              Back
            </button>
            <button className="inline-flex min-h-[44px] cursor-pointer items-center gap-1 px-2 py-2 text-fs-body-md text-fs-text-muted underline-offset-4 transition-colors hover:text-fs-text hover:underline disabled:cursor-default disabled:opacity-40" disabled={remaining <= 0} onClick={onSkip} type="button">
              Skip ›
            </button>
          </div>
        </>}
    </StepShell>;
}
function SwipeCard({
  card,
  depth,
  interactive,
  onResolve,
  setResolver,
  clearResolver
}: {
  card: LabsSwipeCard;
  depth: number;
  interactive: boolean;
  onResolve: (direction: 'left' | 'right') => void;
  setResolver?: (fn: Resolver) => void;
  clearResolver?: (fn: Resolver) => void;
}): ReactNode {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-16, 16]); // ~dragX * 0.08

  // Drag-tied YES/PASS signalling: norm = min(1, abs(dragX) / 140); right drag > 10px → YES, left
  // drag < -10px → PASS. The overlay opacity ramps with drag distance and reaches full strength at
  // ±140px (just past the ±100px commit threshold), so the colored tint + border + stamp are clearly
  // visible BEFORE commit.
  const yesOpacity = useTransform(x, [10, 140], [0, 1]);
  const noOpacity = useTransform(x, [-140, -10], [1, 0]);
  // Stamp scales in slightly as the drag ramps (extra prominence on top of the opacity fade).
  const yesScale = useTransform(x, [10, 140], [0.7, 1]);
  const noScale = useTransform(x, [-140, -10], [1, 0.7]);
  const [exitX, setExitX] = useState(0);
  const resolve = (direction: 'left' | 'right'): void => {
    setExitX(direction === 'right' ? 600 : -600);
    onResolve(direction);
  };

  // Keep the latest `resolve` in a ref, and register a STABLE wrapper with the parent. A stable
  // identity is what makes the parent's identity-guarded `clearResolver` reliable: the just-swiped
  // card's late exit-cleanup only clears the parent ref if it still points at THIS card's wrapper,
  // so it can't clobber the next front card's registration.
  const resolveRef = useRef(resolve);
  resolveRef.current = resolve;
  const stableResolver = useRef<Resolver>(dir => resolveRef.current(dir)).current;
  useEffect(() => {
    if (!(interactive && setResolver && clearResolver)) {
      return;
    }
    setResolver(stableResolver);
    return () => clearResolver(stableResolver);
  }, [interactive, setResolver, clearResolver, stableResolver]);
  const handleDragEnd = (_: unknown, info: PanInfo): void => {
    if (info.offset.x > DRAG_THRESHOLD) {
      resolve('right');
    } else if (info.offset.x < -DRAG_THRESHOLD) {
      resolve('left');
    }
    // else: motion springs x back to 0 via dragSnapToOrigin below.
  };

  // Depth stack: front fully visible; the two behind peek at the BOTTOM via translateY + scale-down.
  // Only the TRANSFORM (y/scale) is animated by Framer — the depth OPACITY is applied through a CSS
  // class (below), NOT through Framer.
  //   depth-1 → translateY(8px) scale(0.96)  + opacity 0.7 (class)
  //   depth-2 → translateY(16px) scale(0.92) + opacity 0.4 (class)
  let depthTransform = {
    y: 0,
    scale: 1
  };
  if (depth === 1) {
    depthTransform = {
      y: 8,
      scale: 0.96
    };
  } else if (depth >= 2) {
    depthTransform = {
      y: 16,
      scale: 0.92
    };
  }

  // FRONT-CARD TRANSPARENCY FIX — why opacity is a CSS CLASS, not a Framer value.
  // AnimatePresence keeps the SAME element mounted as a card is promoted from a faded peek slot
  // (depth 1/2) up to the front (depth 0). If opacity is driven by Framer (`animate`/`style`), the
  // promoted element keeps Framer's last opacity (0.7) — resetting it via the changing `animate`
  // target proved unreliable for a persisting element whose live-drag values (x/rotate) also live in
  // `style`, and a reduced-motion / webdriver short-circuit makes the stale 0.7 stick (the muddy /
  // see-through front card). So we let CSS own the resting opacity: the front card (depth 0) gets NO
  // opacity class → fully OPAQUE; only the peek cards behind it get `opacity-70` / `opacity-40`.
  // Framer never writes a resting inline opacity, so nothing can override the class. (The fly-off
  // `exit` still fades the thrown card — Framer drives opacity imperatively only during exit.)
  let depthOpacityClass = 'opacity-100';
  if (depth === 1) {
    depthOpacityClass = 'opacity-70';
  } else if (depth >= 2) {
    depthOpacityClass = 'opacity-40';
  }
  return <motion.div animate={interactive ? {
    y: 0,
    scale: 1
  } : depthTransform} className={cn('absolute inset-0 flex flex-col items-center justify-center rounded-[20px] border border-fs-border bg-fs-surface px-8 py-9 text-center', 'shadow-[0px_8px_24px_rgba(10,14,26,0.10),0px_2px_6px_rgba(10,14,26,0.05)]', depthOpacityClass,
  // `touch-pan-y` (QA-461): claim horizontal touches for the drag up front, so iOS Safari
  // never arbitrates the swipe as a page pan/zoom gesture. Vertical scroll stays native.
  interactive ? 'z-30 cursor-grab touch-pan-y active:cursor-grabbing' : 'pointer-events-none')} data-fs-depth={depth} data-fs-swipe-card drag={interactive ? 'x' : false} dragConstraints={{
    left: 0,
    right: 0
  }} dragElastic={0.7} dragSnapToOrigin
  // Spring the card back to origin on an incomplete swipe (natural rubber-band return) instead
  // of the default critically-damped snap.
  dragTransition={{
    bounceStiffness: 520,
    bounceDamping: 32
  }}
  // Committed swipe: a quick eased "throw" off-screen (feels like the card was flung) rather
  // than a flat linear slide. The deck's resting state (x=0) is untouched, so the visual
  // snapshot of m1-activity stays deterministic — physics only runs on interaction.
  // `exit` is the ONE place Framer touches opacity (fades the thrown card as it flies off).
  //
  // `zIndex: 40` (QA-436): the instant the throw resolves, the promoted card renders as the new
  // front at zIndex 30 — the SAME zIndex the exiting card froze with — so the tie falls to DOM
  // order and the thrown card can paint BEHIND the deck, sliding out from underneath instead of
  // lifting off the top (obvious on the ✕/✓ button path, where the card exits from center rather
  // than already displaced under the finger). Framer applies the discrete value at exit start,
  // so the thrown card outranks the whole resting stack for the 280ms it is airborne.
  exit={{
    x: exitX,
    opacity: 0,
    zIndex: 40,
    transition: {
      duration: 0.28,
      ease: [0.32, 0, 0.67, 0]
    }
  }}
  // Back/peek cards animate into their resting transform slot; the front card resets its
  // transform (y/scale) to the front slot. Opacity is class-driven, never animated here.
  initial={interactive ? false : depthTransform} onDragEnd={interactive ? handleDragEnd : undefined} style={{
    zIndex: interactive ? 30 : 3 - depth,
    // `pan-y` hands the browser vertical scrolling but reserves HORIZONTAL gestures for the drag.
    // Without it, iOS Safari treats the sideways swipe as an ambiguous scroll and partly hijacks
    // it — the card lags/jumps and the swipe "doesn't feel the same" (reported on mobile). Only
    // the interactive front card drags, so only it needs this.
    touchAction: interactive ? 'pan-y' : undefined,
    // Only the live-drag channels (x/rotate) live in style for the front card; the peek cards
    // get their resting transform from `animate` below. Opacity is intentionally NOT here (CSS
    // class owns it — see depthOpacityClass).
    ...(interactive ? {
      x,
      rotate
    } : depthTransform)
  }}>
      {/* YES / PASS drag overlays (front card only): a FULL-CARD tinted + colored-bordered overlay
          with a big rotated stamp, opacity ramping with drag distance. Green (fs-success) =
          YES/right, red (fs-danger) = PASS/left. Absolutely-positioned, inset-0, pointer-events-none,
          pinned to the card radius — they never shift the deck and only appear during an active drag. */}
      {interactive ? <>
          <motion.div aria-hidden className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-[20px] border-[3px] border-fs-success" style={{
        opacity: yesOpacity,
        backgroundColor: 'rgba(0, 110, 32, 0.15)'
      }}>
            <motion.span className="font-extrabold font-inter text-[2.5rem] text-fs-success uppercase leading-none tracking-[0.05em]" style={{
          scale: yesScale,
          rotate: -12
        }}>
              Yes
            </motion.span>
          </motion.div>
          <motion.div aria-hidden className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-[20px] border-[3px] border-fs-danger" style={{
        opacity: noOpacity,
        backgroundColor: 'rgba(220, 38, 38, 0.12)'
      }}>
            <motion.span className="font-extrabold font-inter text-[2.5rem] text-fs-danger uppercase leading-none tracking-[0.05em]" style={{
          scale: noScale,
          rotate: 12
        }}>
              Pass
            </motion.span>
          </motion.div>
        </> : null}

      {/* Emoji icon (~4.5rem) */}
      <span aria-hidden className="mb-5 leading-none" style={{
      fontSize: '4.5rem'
    }}>
        {card.icon}
      </span>
      {/* Activity line (serif ~1.9rem, max-w 280) */}
      <p className="max-w-[280px] font-instrument text-[1.9rem] text-fs-text leading-[1.15] tracking-[-0.01em]">
        {card.text}
      </p>
      {/* Bottom hint — only on the front card */}
      <span className="mt-auto pt-6 text-[11px] text-fs-text-muted uppercase tracking-[0.06em]">
        {interactive ? 'Swipe or tap a button' : ''}
      </span>
    </motion.div>;
}

/** The round ✕ / ✓ action button below the deck. */
function ActionButton({
  children,
  tone,
  label,
  onClick,
  disabled
}: {
  children: ReactNode;
  tone: 'yes' | 'no';
  label: string;
  onClick: () => void;
  disabled?: boolean;
}): ReactNode {
  return <button aria-label={label} className={cn('flex h-14 w-14 cursor-pointer items-center justify-center rounded-fs-pill border-2 border-fs-border bg-fs-surface text-fs-text-subtle transition-all duration-200 disabled:cursor-default', 'disabled:opacity-40 motion-safe:hover:scale-105', tone === 'no' ? 'hover:border-fs-danger hover:text-fs-danger' : 'hover:border-fs-success hover:text-fs-success')} disabled={disabled} onClick={onClick} type="button">
      {children}
    </button>;
}